import { useEffect } from "react";
import { isAudioUnlocked, unlockAudio } from "@/lib/audioUnlock";

/**
 * Global, one-time mobile audio unlock.
 *
 * iOS/Android browsers often block programmatic audio playback until the user
 * interacts with the page. We listen for the first user gesture and attempt
 * to unlock audio immediately (capture phase), then remove listeners once done.
 */
export default function AudioUnlockListener() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    let cleanedUp = false;

    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      window.removeEventListener("pointerdown", onGesture, true);
      window.removeEventListener("touchstart", onGesture, true);
      window.removeEventListener("keydown", onGesture, true);
    };

    const onGesture = () => {
      if (isAudioUnlocked()) {
        cleanup();
        return;
      }

      // Fire-and-forget: unlockAudio internally dedupes attempts.
      unlockAudio().finally(() => {
        if (isAudioUnlocked()) cleanup();
      });
    };

    // Capture-phase so we run even when navigation happens on the same tap.
    window.addEventListener("pointerdown", onGesture, { capture: true, passive: true });
    window.addEventListener("touchstart", onGesture, { capture: true, passive: true });
    window.addEventListener("keydown", onGesture, { capture: true });

    return cleanup;
  }, []);

  return null;
}
